import type { DeployedReleaseManifest } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import type { Release } from '@sunstone/api-client/gen/releases/v1/releases_pb';
import { ReleaseDiffStatus, usePreviewReleaseDiff } from '@sunstone/api-client/hooks';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@sunstone/ui';
import { GitCompare, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DeployedComponentsPanel } from '@/components/ui/DeployedComponentsPanel';
import { EmptyState } from '@/components/ui/EmptyState';
import { ReleaseDiffTable } from '@/components/ui/ReleaseDiffTable';
import type { ComponentFamily } from '@/features/workspaces/componentFamily';
import { errorToastMessage } from '@/lib/errors';

export interface ComponentsDiffPanelProps {
  workspaceId: string;
  token?: string | null;
  /** The workspace's own deployed manifest (same value DeployedComponentsPanel
   * takes) - shown plainly by default, and used as the diff's "before" side
   * once a release is picked. */
  deployedManifest?: DeployedReleaseManifest;
  /** Raw lowercase family values keyed by service name - threaded straight
   * through to DeployedComponentsPanel's Type column/filter. */
  familyByServiceName?: Record<string, string>;
  canDeployFamily?: (family: ComponentFamily) => boolean;
  onDeployFamily?: (family: ComponentFamily) => void;
  /** The workspace's own current release - offered in the picker as a
   * drift-check option (deployed manifest vs. that release's own synced
   * manifest, which can differ from what's deployed even with no version
   * bump, e.g. after a resync). Distinct from the default "Current
   * components" option, which never calls the diff RPC at all. */
  currentReleaseId: string;
  currentVersion: string;
  /** Releases newer than the workspace's current release (same list
   * WorkspaceDetail already computes for the Upgrade tab via
   * useListAvailableReleases - see ListAvailableReleases's own doc comment,
   * workspace_service.go). Deliberately NOT the whole release catalog - the
   * picker only ever offers upgrade candidates plus the current release
   * itself, not every deployable release regardless of version. */
  availableReleases: Release[];
  /** Gates the mutate effect so this panel does no work while its tab isn't
   * selected. */
  active: boolean;
}

/**
 * Workspace Detail's "Components" tab - shows the workspace's own deployed
 * manifest plainly by default (the same DeployedComponentsPanel used for a
 * legacy/no-release workspace), so seeing what's actually deployed is never
 * obscured by diff chrome. Diffing is opt-in via the picker on the right,
 * which offers the current release (a drift check - see currentReleaseId's
 * doc comment) and any newer releases (availableReleases); it never offers
 * the whole catalog. Clearing the selection returns to the plain view.
 * Upgrade previews against a specific target still live in the Promote
 * dialog - this picker is purely a "what would change" look, not a promote
 * entry point.
 *
 * Once a release is picked, three cases show a dedicated EmptyState instead
 * of a diff table, all intentional:
 * - The picked release has never synced (RELEASE_DIFF_STATUS_SYNC_NEEDED -
 *   the one remaining hard gate from docs/design/005-release-sync-and-
 *   promote.md) - there's nothing to diff against yet, and syncing is done
 *   from the release list or the Promote dialog, not from here.
 * - Any other diff-load error (e.g. the picked release has since been
 *   deprecated/disabled).
 * - The diff computation itself failed (RELEASE_DIFF_STATUS_FAILED).
 * These are deliberately distinct from "no manifest deployed yet" and "no
 * selection," which use the plain DeployedComponentsPanel/its own built-in
 * empty state instead - those aren't "a diff was attempted and came back
 * unavailable" cases.
 */
export function ComponentsDiffPanel({
  workspaceId,
  token,
  deployedManifest,
  familyByServiceName,
  canDeployFamily,
  onDeployFamily,
  currentReleaseId,
  currentVersion,
  availableReleases,
  active,
}: ComponentsDiffPanelProps) {
  const [selectedReleaseId, setSelectedReleaseId] = useState('');
  const previewDiff = usePreviewReleaseDiff(token);

  // Re-fires on tab-select or on picking/clearing a release - never on its
  // own initiative (e.g. an SSE-driven cache invalidation of the workspace
  // query elsewhere), matching this panel's documented lack of live re-fire
  // on sync completion.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally omits previewDiff
  useEffect(() => {
    if (!active || !workspaceId || !deployedManifest || !selectedReleaseId) return;
    previewDiff.mutate(
      { workspaceId, releaseId: selectedReleaseId },
      {
        onError: (err) =>
          toast.error(errorToastMessage(err, 'Failed to compute the release diff.')),
      },
    );
  }, [active, workspaceId, deployedManifest, selectedReleaseId]);

  const NONE_VALUE = '__none__';
  const hasPickableReleases = !!currentReleaseId || availableReleases.length > 0;
  const picker = hasPickableReleases && (
    <div className="flex items-center justify-end gap-1.5">
      <span className="text-xs text-muted-foreground font-medium">Compare to:</span>
      <Select
        value={selectedReleaseId || NONE_VALUE}
        onValueChange={(v) => setSelectedReleaseId(v === NONE_VALUE ? '' : v)}
      >
        <SelectTrigger className="h-7 w-48 text-xs">
          <SelectValue placeholder="Select a release…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>Current components</SelectItem>
          {currentReleaseId && (
            <SelectItem value={currentReleaseId}>{currentVersion} (current release)</SelectItem>
          )}
          {availableReleases.map((r) => (
            <SelectItem key={r.releaseId} value={r.releaseId}>
              {r.version}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  if (!deployedManifest) {
    return <DeployedComponentsPanel manifest={undefined} />;
  }

  if (!selectedReleaseId) {
    return (
      <div className="space-y-4">
        {picker}
        <DeployedComponentsPanel
          manifest={deployedManifest}
          familyByServiceName={familyByServiceName}
          canDeployFamily={canDeployFamily}
          onDeployFamily={onDeployFamily}
        />
      </div>
    );
  }

  const data = previewDiff.data;

  return (
    <div className="space-y-4">
      {picker}
      {previewDiff.isPending && (
        <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">Loading…</p>
        </div>
      )}
      {!previewDiff.isPending && previewDiff.error && (
        <EmptyState
          icon={<GitCompare className="h-6 w-6 text-muted-foreground" />}
          title="No diff available"
          description={previewDiff.error.message || 'Failed to load the diff for this release.'}
        />
      )}
      {!previewDiff.isPending &&
        !previewDiff.error &&
        data?.status === ReleaseDiffStatus.SYNC_NEEDED && (
          <EmptyState
            icon={<GitCompare className="h-6 w-6 text-muted-foreground" />}
            title="No diff available"
            description="This release hasn't synced yet, so there's nothing to compare against."
          />
        )}
      {!previewDiff.isPending &&
        !previewDiff.error &&
        data?.status === ReleaseDiffStatus.FAILED && (
          <EmptyState
            icon={<GitCompare className="h-6 w-6 text-muted-foreground" />}
            title="No diff available"
            description={data.errorMessage || 'The release diff failed to compute.'}
          />
        )}
      {!previewDiff.isPending && !previewDiff.error && data?.status === ReleaseDiffStatus.READY && (
        <ReleaseDiffTable diff={data.diff} />
      )}
    </div>
  );
}
